import { Grid } from '@material-ui/core';
import { pick } from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import ActionWindowGrossWeightLine from 'src/action-window/components/gross-weight-line';
import ActionWindowLinkedLines from 'src/action-window/components/linked-lines';
import ActionWindowManualTareLine from 'src/action-window/components/manual-or-no-tare-line';
import { EJRecordTypes } from 'src/common/enumerations/ej-record-types';
import { TenderReferences } from 'src/common/enumerations/tender-reference-codes';
import { TenderType } from 'src/common/enumerations/tender-type';
import { calculateGrossWeight } from 'src/common/helpers/action-window-helper';
import { itemMeasureAttributes } from 'src/common/helpers/item-quantity-measure';
import { validateWeight } from 'src/common/helpers/weight-uom-validate';
import { ILineItem } from 'src/common/interfaces/dtos/ILineItem';
import {
  IElectronicJournalRecord,
  TbrData,
} from 'src/common/interfaces/states/IElectronicJournalRecord';
import { AppState, store } from 'src/common/redux/configure-store';
import { CartItemViewData } from 'src/items-list/business-logic/service/interfaces/cart-response';
import { ReturnsType } from 'src/transaction-search/interfaces/get-effective-return-policy-response';
import ProductVoidedItem from '../action-window/components/product-voided-item';
import { TenderIcon } from './components/tender-icon';
import TopItemDetails, { ItemDetailType } from './components/top-item-details';
import TopReturnItemDetails from './components/top-return-item-details';
import { styles } from './top-customer-advert-styles';
import WeightInfo from './weight-info';
import { styles as weightStyles } from './weight-info-styles';

const TopAdvert: React.FC = (props: ReturnType<typeof mapStateToProps>): JSX.Element | null => {
  const total = props.itemState.total;
  const ejItems: IElectronicJournalRecord[] = props.itemState.ejItems;
  const { t } = useTranslation();

  const selectedItem: IElectronicJournalRecord = ejItems.find(item => item.Selected) || ejItems[0];
  const classes = styles();
  const weightClasses = weightStyles();

  if (ejItems.length === 0) {
    return null;
  }

  const UOMPrecision: number = store.getState().touchPoint.touchPointConfiguration
    .defaultWeightUnitOfMeasurePrecision;
  const item: CartItemViewData | undefined = props.itemState.items?.find(
    i => i.lineId === selectedItem?.SequenceNumber,
  );
  const isTareOverride: boolean = !!selectedItem?.TareWeight?.IsTareOverride;
  const tbrData: TbrData = pick(selectedItem, ['tbrAvailableQty', 'ExtendedAmount']);
  const isTBRActive: boolean =
    store.getState().posMode.returnMode.returnType === ReturnsType.TransactionBasedReturn;
  const isSelectedItemReturnItem: boolean | undefined = isTBRActive
    ? selectedItem?.tbrReturnItem
    : selectedItem?.Type === EJRecordTypes.ReturnItem;
  const atomicMsuUnit: string = item
    ? itemMeasureAttributes(item, tbrData, isTBRActive).atomicMsuUnit
    : '';
  const isLinkedTare = !!selectedItem?.LinkedLines?.length;
  const isManualTare = isTareOverride && !isLinkedTare;

  const linkedLineList = selectedItem.LinkedLines?.map((linkedItem: ILineItem) => {
    return (
      <Grid
        key={`${selectedItem.ItemId}-${linkedItem.SequenceNumber}`}
        container
        className={`${weightClasses.lineItem} ${classes.linkedItemContainer}`}
      >
        <Grid container item className='' data-testid='customer-display-quantity-line-container'>
          {!isTBRActive &&
            item?.linkedLines?.map((lineitem: CartItemViewData) => (
              <ActionWindowLinkedLines
                key={lineitem.description}
                linkedLine={lineitem}
                parentLine={item}
                isSelectedItemReturnItem={isSelectedItemReturnItem}
                atomicMsuUnit={lineitem.price?.unitOfMeasure ?? atomicMsuUnit}
              />
            ))}
        </Grid>
      </Grid>
    );
  });

  if (selectedItem.Type === EJRecordTypes.ReturnItem) {
    return <TopReturnItemDetails item={selectedItem}></TopReturnItemDetails>;
  }
  const getTenderText = (tenderType: string): string => {
    switch (tenderType) {
      case TenderType.CreditDebit:
        return 'CreditDebitTender';
      case TenderType.Cash:
        return 'CashTender';
      case TenderType.Check:
        return 'CheckTender';
      case EJRecordTypes.RewardAsTender:
        return 'RewardTender';
      case TenderType.WicCvv:
      case TenderType.Ewic:
        return 'eWICTender';
      default:
        return '';
    }
  };

  const tenderCheck = checkForTender(selectedItem, total);
  const isGiftCard: boolean =
    selectedItem.TenderReferences?.includes(TenderReferences.GiftCard) ?? false;

  if (tenderCheck) {
    return (
      <Grid container className={classes.tenderContainer}>
        <Grid className={classes.row}>
          <Grid data-testid='top-customer-advert-tenderText' className={classes.tenderText}>
            {isGiftCard
              ? t('PaidTender', { arg: selectedItem.Name })
              : t(getTenderText(tenderCheck))}
          </Grid>
        </Grid>
        <Grid data-testid='top-customer-advert-tenderIcon' className={classes.row}>
          <TenderIcon tenderType={tenderCheck} />
        </Grid>
      </Grid>
    );
  }

  if (selectedItem?.Voided) {
    return (
      <Grid data-testid='voided-item' container className={`fullHeight ${classes.gridSpace}`}>
        <ProductVoidedItem isKeypadVisible={false} itemType={EJRecordTypes.CustomerItemInfo} />
      </Grid>
    );
  }

  return (
    <>
      <Grid container className={classes.titleContainer}>
        <Grid data-testid='top-customer-advert-title' item xs={10} className={classes.headerText}>
          <TenderIcon tenderType={selectedItem.Type} />
          <span className={classes.titleText}>
            <TopItemDetails item={selectedItem} type={ItemDetailType.Title} />
          </span>
        </Grid>
        <Grid data-testid='top-customer-advert-price' item xs={2} className={classes.priceText}>
          <TopItemDetails item={selectedItem} type={ItemDetailType.Price} />
        </Grid>
      </Grid>
      {selectedItem && isTareOverride && (
        <ActionWindowGrossWeightLine
          grossWeight={calculateGrossWeight(
            selectedItem.TareWeight,
            selectedItem.Quantity,
            UOMPrecision,
          )}
          atomicMsuUnit={atomicMsuUnit}
        />
      )}
      {validateWeight(selectedItem.UnitOfMeasureCode) && (
        <Grid item xs={12} className={classes.validateWeightContainer}>
          <WeightInfo selectedItem={selectedItem} />
        </Grid>
      )}
      {linkedLineList}
      {selectedItem && isManualTare && (
        <ActionWindowManualTareLine
          tareWeight={selectedItem.TareWeight}
          atomicMsuUnit={atomicMsuUnit}
        />
      )}
      {!validateWeight(selectedItem.UnitOfMeasureCode) && (
        <Grid container className={classes.codeText}>
          <Grid item className={classes.itemDescFlexBox} />
          <Grid data-testid='top-customer-advert-text' className={classes.advertText}>
            <TopItemDetails item={selectedItem} type={ItemDetailType.Description} />
          </Grid>
        </Grid>
      )}
    </>
  );
};

const mapStateToProps = (state: AppState): ReturnType<typeof mapStateToProps> => ({
  itemState: state.itemList,
  tenderingState: state.tendering,
});

export const checkForTender = (
  ejItems: IElectronicJournalRecord,
  total: number,
): string | undefined => {
  if (ejItems.Type === EJRecordTypes.RewardAsTender && total !== 0) {
    return ejItems.Type;
  }
  if (ejItems.TenderType && total !== 0) {
    return ejItems.TenderType;
  }
};

export default connect(mapStateToProps)(TopAdvert);
